/**
 * SRAC Club Calendar
 *
 * Month-grid calendar for the public events page: renders event pills (with
 * practice-race styling and hover details), a selected-day detail panel, and
 * an upcoming-events list. Its grid Firestore listeners are bounded to the
 * month on screen plus one month either side, re-subscribing as the visitor
 * pages, while the upcoming list uses its own from-today query limited to
 * three events, so viewing the calendar never reads the entire events
 * collection and the upcoming list stays independent of paging.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import * as React from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isFuture,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  parseISO,
} from 'date-fns';

import { parseEventDate } from '@/lib/date-utils';
import { ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Event as FirebaseEvent, EventType } from '@/models/event';
import type { RaceEvent } from '@/models/competitions/race-event';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Dialog,
  DialogContent,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFirestore, useCollection } from '@/firebase';
import { collection, query, orderBy, where, limit, Timestamp, Query } from 'firebase/firestore';

function useCalendar() {
  const firestore = useFirestore();
  // Start at the actual current month, not hardcoded to January 2025
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [selectedDay, setSelectedDay] = React.useState<Date | null>(null);

  // Listener window: the displayed month plus one either side. Unbounded
  // listeners on `events` / `raceEvents` read every document on every page
  // view, which is what exhausted the daily Firestore read quota. The upper
  // bound is EXCLUSIVE because some `date` values carry a time suffix
  // (`2026-09-30T10:00`), which sorts after the plain last-day string.
  const windowStart = format(startOfMonth(addMonths(currentMonth, -1)), 'yyyy-MM-dd');
  const windowEnd = format(startOfMonth(addMonths(currentMonth, 2)), 'yyyy-MM-dd');

  // Fetch events and event types from Firebase
  const eventsQuery = React.useMemo(() => {
    return query(
      collection(firestore, 'events'),
      where('date', '>=', windowStart),
      where('date', '<', windowEnd),
      orderBy('date', 'asc'),
    ) as Query<FirebaseEvent>;
  }, [firestore, windowStart, windowEnd]);

  const eventTypesQuery = React.useMemo(() => {
    return query(collection(firestore, 'eventTypes')) as Query<EventType>;
  }, [firestore]);

  // Race events are needed so calendar pills synced from a race can
  // surface the practice flag (and any other race-only metadata) without
  // requiring the server-side event-sync step to denormalise it onto the
  // event document.
  // Bounded to the same window as the events listener — `raceEvents.date` is a
  // Timestamp, so the boundaries are Timestamps rather than date strings.
  const raceEventsQuery = React.useMemo(() => {
    return query(
      collection(firestore, 'raceEvents'),
      where('date', '>=', Timestamp.fromDate(parseISO(windowStart))),
      where('date', '<', Timestamp.fromDate(parseISO(windowEnd))),
    ) as Query<RaceEvent>;
  }, [firestore, windowStart, windowEnd]);

  // The upcoming list gets its own query rather than reusing the grid window:
  // it must not follow calendar paging, and a club gap longer than the window
  // would otherwise read as "no upcoming events" on a public page. Three docs.
  const today = format(new Date(), 'yyyy-MM-dd');
  const upcomingEventsQuery = React.useMemo(() => {
    return query(
      collection(firestore, 'events'),
      where('date', '>=', today),
      orderBy('date', 'asc'),
      limit(3),
    ) as Query<FirebaseEvent>;
  }, [firestore, today]);

  const { data: events, loading: eventsLoading } = useCollection<FirebaseEvent>(eventsQuery);
  const { data: upcomingEventsData } = useCollection<FirebaseEvent>(upcomingEventsQuery);
  const { data: eventTypes, loading: typesLoading } = useCollection<EventType>(eventTypesQuery);
  const { data: raceEvents } = useCollection<RaceEvent>(raceEventsQuery);

  /**
   * Set of raceEvent ids that are flagged as practice. Used by the
   * calendar grid to render those pills with a transparent background
   * + dotted border instead of the usual filled chip.
   */
  const practiceRaceIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (!raceEvents) return ids;
    for (const r of raceEvents) {
      if (r.isPractice) ids.add(r.id);
    }
    return ids;
  }, [raceEvents]);

  // Debug logging
  React.useEffect(() => {
    if (events) {
      console.log('Events loaded:', events.length, 'events');
      console.log('Sample event:', events[0]);
    }
  }, [events]);

  const firstDay = startOfWeek(startOfMonth(currentMonth));
  const lastDay = endOfWeek(endOfMonth(currentMonth));

  const days = eachDayOfInterval({ start: firstDay, end: lastDay });

  // All events organized by date for calendar display
  const eventsByDate = React.useMemo(() => {
    if (!events) return {};
    return events.reduce((acc: { [key: string]: FirebaseEvent[] }, event) => {
      const dateKey = event.date; // Already in YYYY-MM-DD format
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(event);
      return acc;
    }, {});
  }, [events]);

  // Debug logging for eventsByDate
  React.useEffect(() => {
    console.log('=== CALENDAR DEBUG ===');
    console.log('Current month:', format(currentMonth, 'MMMM yyyy'));
    console.log('eventsByDate keys:', Object.keys(eventsByDate));
    console.log('eventsByDate full object:', eventsByDate);

    // Log events for current month
    const currentMonthStr = format(currentMonth, 'yyyy-MM');
    const currentMonthEvents = Object.entries(eventsByDate).filter(([date]) =>
      date.startsWith(currentMonthStr)
    );
    console.log(`Events in ${currentMonthStr}:`, currentMonthEvents);
    console.log('Total events in current month:', currentMonthEvents.length);

    // Log a few sample days from the calendar
    const sampleDays = days.slice(0, 7);
    console.log('Sample days (first week):', sampleDays.map(d => format(d, 'yyyy-MM-dd')));
    console.log('Sample days have events?:', sampleDays.map(d => {
      const key = format(d, 'yyyy-MM-dd');
      return { date: key, hasEvent: !!eventsByDate[key], events: eventsByDate[key] };
    }));
  }, [eventsByDate, currentMonth, days]);

  const upcomingEvents = React.useMemo(() => {
    if (!upcomingEventsData) return [];

    // Firestore already bounded and ordered these; the filter/sort/slice is a
    // cheap safety net for date values carrying a time suffix.
    const futureEvents = upcomingEventsData
      .filter((event) => {
        // Compare date strings directly (YYYY-MM-DD >= YYYY-MM-DD)
        // Ensure we compare date parts only, robust against ISO strings
        return event.date.split('T')[0] >= today;
      })
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 3);

    console.log('=== UPCOMING EVENTS DEBUG ===');
    console.log('Total events:', upcomingEventsData.length);
    console.log('Today:', today);
    console.log('Future events found:', futureEvents.length);
    console.log('Future events:', futureEvents.map(e => ({ date: e.date, title: e.title })));

    return futureEvents;
  }, [upcomingEventsData, today]);

  const nextMonth = () => setCurrentMonth((prev) => addMonths(prev, 1));
  const prevMonth = () => setCurrentMonth((prev) => addMonths(prev, -1));

  const getEventTypeName = (typeId: string) => {
    return eventTypes?.find(t => t.id === typeId)?.name || '';
  };

  const getEventTypeColor = (typeId: string): string => {
    // Get the hex color from the event type
    const eventType = eventTypes?.find(t => t.id === typeId);
    if (eventType?.color) {
      return eventType.color; // Returns hex like '#3B82F6'
    }

    // Default fallback color
    return '#6B7280'; // Gray
  };

  /**
   * Returns true when the event is paired with a race that is flagged
   * `isPractice`. Returns false for events with no source race or
   * before the raceEvents collection has finished loading.
   */
  const isEventPractice = (event: FirebaseEvent): boolean => {
    if (!event.sourceRaceEventId) return false;
    return practiceRaceIds.has(event.sourceRaceEventId);
  };

  return {
    currentMonth,
    days,
    selectedDay,
    setSelectedDay,
    nextMonth,
    prevMonth,
    eventsByDate,
    upcomingEvents,
    loading: eventsLoading || typesLoading,
    getEventTypeName,
    getEventTypeColor,
    isEventPractice,
  };
}

/**
 * Hover-card content for a calendar pill. Surfaces the title, full
 * date, location, time window, event-type badge, and a Practice
 * indicator when the underlying race is flagged `isPractice`.
 */
function CalendarPillTooltip({
  event,
  isPractice,
  eventTypeName,
  accentColor,
  date,
}: {
  event: FirebaseEvent;
  isPractice: boolean;
  eventTypeName: string;
  accentColor: string;
  date: Date;
}) {
  return (
    <div className="space-y-1.5 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-sm leading-tight">{event.title}</div>
        {isPractice && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded border border-dotted whitespace-nowrap font-medium"
            style={{ borderColor: accentColor, color: accentColor }}
          >
            Practice
          </span>
        )}
      </div>
      <div className="text-muted-foreground">{format(date, 'EEEE, MMMM do, yyyy')}</div>
      {eventTypeName && (
        <div>
          <span
            className="inline-block text-[10px] px-1.5 py-0.5 rounded-full text-white font-medium"
            style={{ backgroundColor: accentColor }}
          >
            {eventTypeName}
          </span>
        </div>
      )}
      {event.location && (
        <div>
          <span className="text-muted-foreground">Location: </span>
          {event.location}
        </div>
      )}
      {event.time && (
        <div>
          <span className="text-muted-foreground">Time: </span>
          {event.time}
        </div>
      )}
      {event.cd && (
        <div>
          <span className="text-muted-foreground">CD: </span>
          {event.cd}
        </div>
      )}
      {event.description && (
        <div className="text-muted-foreground whitespace-pre-line pt-1 border-t">
          {event.description}
        </div>
      )}
    </div>
  );
}

function EventDetails({
  selectedDay,
  events,
  getEventTypeName,
  getEventTypeColor,
  onClose,
}: {
  selectedDay: Date | null;
  events: FirebaseEvent[];
  getEventTypeName: (typeId: string) => string;
  getEventTypeColor: (typeId: string) => string;
  onClose?: () => void;
}) {
  if (!selectedDay) return null;

  const displayDate = selectedDay || new Date();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>
            {format(displayDate, 'MMMM do, yyyy')}
          </CardTitle>
          <CardDescription>
            {format(displayDate, 'eeee')}
          </CardDescription>
        </div>
        {onClose && (
          <DialogClose asChild>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </DialogClose>
        )}
      </CardHeader>
      <CardContent>
        {events.length > 0 ? (
          <div className="space-y-4">
            {events.map((event) => {
              const typeColor = getEventTypeColor(event.typeId);
              return (
                <div key={event.id} className="p-3 rounded-lg border bg-card">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold">{event.title}</h4>
                    <span
                      className="text-xs px-2 py-1 rounded-full whitespace-nowrap text-white font-medium"
                      style={{ backgroundColor: typeColor }}
                    >
                      {getEventTypeName(event.typeId)}
                    </span>
                  </div>
                  {event.description && (
                    <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                  )}
                  {event.time && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {event.time}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">
            No events on this day.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function UpcomingEvents({
  events,
  getEventTypeName,
  getEventTypeColor
}: {
  events: FirebaseEvent[];
  getEventTypeName: (typeId: string) => string;
  getEventTypeColor: (typeId: string) => string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Upcoming Events</CardTitle>
      </CardHeader>
      <CardContent>
        {events.length > 0 ? (
          <div className="space-y-4">
            {events.map((event) => {
              const typeColor = getEventTypeColor(event.typeId);
              const eventDate = parseEventDate(event.date);
              return (
                <div key={event.id} className="flex items-center gap-4 p-3 rounded-lg border bg-card hover:bg-secondary/50 transition-colors">
                  <div className="flex-shrink-0 text-center w-14">
                    <p className="text-sm font-semibold text-primary">
                      {format(eventDate, 'MMM')}
                    </p>
                    <p className="text-xl font-bold">
                      {format(eventDate, 'dd')}
                    </p>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm">{event.title}</h4>
                    <p className="text-xs text-muted-foreground">
                      {format(eventDate, 'eeee')}
                      {event.time ? `, ${event.time}` : ''}
                    </p>
                    <span
                      className="inline-block text-xs px-2 py-0.5 rounded-full text-white font-medium mt-1"
                      style={{ backgroundColor: typeColor }}
                    >
                      {getEventTypeName(event.typeId)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">
            No upcoming events scheduled.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function ClubCalendar() {
  const {
    currentMonth,
    days,
    selectedDay,
    setSelectedDay,
    nextMonth,
    prevMonth,
    eventsByDate,
    upcomingEvents,
    loading,
    getEventTypeName,
    getEventTypeColor,
    isEventPractice,
  } = useCalendar();
  const isMobile = useIsMobile();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleDayClick = (day: Date) => {
    setSelectedDay(day);
    if (isMobile) {
      setIsModalOpen(true);
    }
  };

  // Get events for selected day, or today if no selection
  const displayDay = selectedDay || new Date();
  const displayDayEvents = eventsByDate[format(displayDay, 'yyyy-MM-dd')] || [];

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mr-2" />
          <span>Loading calendar...</span>
        </CardContent>
      </Card>
    );
  }

  const CalendarGrid = () => {
    // Debug: Log what we're rendering
    React.useEffect(() => {
      console.log('CalendarGrid rendering with eventsByDate:', eventsByDate);
      console.log('Total event dates:', Object.keys(eventsByDate).length);

      // Check first few days
      const sampleDays = days.slice(0, 10);
      sampleDays.forEach(day => {
        const dayKey = format(day, 'yyyy-MM-dd');
        const dayEvents = eventsByDate[dayKey] || [];
        if (dayEvents.length > 0) {
          console.log(`Day ${dayKey} has ${dayEvents.length} events:`, dayEvents.map(e => e.title));
        }
      });
    }, []);

    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="icon" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h2 className="text-lg font-semibold text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <Button variant="outline" size="icon" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-7 text-center text-sm font-medium text-muted-foreground border-b mb-2 pb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const dayKey = format(day, 'yyyy-MM-dd');
              const dayEvents = eventsByDate[dayKey] || [];
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isSelected = selectedDay && isSameDay(day, selectedDay);
              const isTodayDate = isToday(day);

              // Log for debugging if this day has events
              if (dayEvents.length > 0) {
                console.log(`Rendering day ${dayKey} with ${dayEvents.length} events`);
              }

              return (
                <div
                  key={day.toString()}
                  onClick={() => handleDayClick(day)}
                  className={`
                    relative flex flex-col items-start justify-start min-h-24 sm:min-h-32 p-1 border rounded-md cursor-pointer transition-colors hover:bg-secondary/50
                    ${isCurrentMonth ? 'bg-card' : 'bg-muted/50'}
                    ${isSelected && !isTodayDate && 'ring-2 ring-primary'}
                    ${isTodayDate && 'ring-2 ring-primary bg-primary/5'}
                  `}
                >
                  {/* Day number */}
                  <span className={`
                    text-xs sm:text-sm font-semibold mb-1
                    ${!isCurrentMonth && 'text-muted-foreground'}
                    ${isTodayDate && 'text-primary'}
                  `}>
                    {format(day, 'd')}
                  </span>

                  {/* Event pills */}
                  <div className="w-full space-y-0.5">
                    {dayEvents.slice(0, 3).map((event) => {
                      const accentColor = getEventTypeColor(event.typeId);
                      const practice = isEventPractice(event);
                      // Practice pills: transparent fill + dotted border
                      // in the event-type accent color so they read as
                      // visually distinct from scoring races. Non-practice
                      // pills keep the existing solid fill.
                      const pillStyle: React.CSSProperties = practice
                        ? {
                            backgroundColor: 'transparent',
                            color: accentColor,
                            borderColor: accentColor,
                          }
                        : { backgroundColor: accentColor };
                      const pillClass = practice
                        ? 'text-[0.6rem] sm:text-xs px-1 py-0.5 rounded truncate w-full font-medium border border-dotted'
                        : 'text-[0.6rem] sm:text-xs px-1 py-0.5 rounded truncate w-full text-white font-medium';
                      return (
                        <Tooltip key={event.id}>
                          <TooltipTrigger asChild>
                            <div
                              className={pillClass}
                              style={pillStyle}
                              onClick={(e) => {
                                // Tooltip lives inside the day cell — stop
                                // the click from propagating so hovering /
                                // tapping a pill doesn't double-trigger the
                                // day-cell click handler.
                                e.stopPropagation();
                                handleDayClick(day);
                              }}
                            >
                              {event.title}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <CalendarPillTooltip
                              event={event}
                              isPractice={practice}
                              eventTypeName={getEventTypeName(event.typeId)}
                              accentColor={accentColor}
                              date={day}
                            />
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-[0.6rem] text-muted-foreground px-1">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };


  if (isMobile) {
    return (
      <TooltipProvider delayDuration={150}>
      <div className="space-y-8">
        <CalendarGrid />
        <EventDetails
          selectedDay={displayDay}
          events={displayDayEvents}
          getEventTypeName={getEventTypeName}
          getEventTypeColor={getEventTypeColor}
        />
        <UpcomingEvents
          events={upcomingEvents}
          getEventTypeName={getEventTypeName}
          getEventTypeColor={getEventTypeColor}
        />
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <EventDetails
              selectedDay={selectedDay}
              events={eventsByDate[format(selectedDay || new Date(), 'yyyy-MM-dd')] || []}
              getEventTypeName={getEventTypeName}
              getEventTypeColor={getEventTypeColor}
              onClose={() => setIsModalOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div className="grid md:grid-cols-3 gap-8">
      <div className="md:col-span-2">
        <CalendarGrid />
      </div>
      <div className="space-y-8">
        <EventDetails
          selectedDay={displayDay}
          events={displayDayEvents}
          getEventTypeName={getEventTypeName}
          getEventTypeColor={getEventTypeColor}
        />
        <UpcomingEvents
          events={upcomingEvents}
          getEventTypeName={getEventTypeName}
          getEventTypeColor={getEventTypeColor}
        />
      </div>
    </div>
    </TooltipProvider>
  );
}
